import { Link } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface City {
  id: number;
  name: string;
  aliases: string[];
  active: boolean;
}

interface Props {
  cities: City[];
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

export default function CanonicalCities({ cities: initial }: Props) {
  const [cities, setCities]   = useState<City[]>(initial);
  const [name, setName]       = useState("");
  const [aliases, setAliases] = useState("");
  const [adding, setAdding]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [filter, setFilter]   = useState("");

  const filtered = filter.trim()
    ? cities.filter((c) =>
        c.name.includes(filter.toLowerCase()) ||
        c.aliases.some((a) => a.includes(filter.toLowerCase()))
      )
    : cities;

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const res = await fetch("/settings/canonical-cities", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          canonical_city: {
            name:    name.trim(),
            aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.errors ? JSON.stringify(data.errors) : "Failed to add city.");
        return;
      }
      setCities((prev) => [...prev, data as City].sort((a, b) => a.name.localeCompare(b.name)));
      setName("");
      setAliases("");
    } catch {
      setError("Failed to add city. Try again.");
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(city: City) {
    if (!confirm(`Remove "${city.name}" from canonical cities?`)) return;
    try {
      const res = await fetch(`/settings/canonical-cities/${city.id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      if (res.ok) setCities((prev) => prev.filter((c) => c.id !== city.id));
    } catch {
      // silent — city stays in list
    }
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link href="/settings" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Canonical Cities</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Cities used for DNC office scope matching. Add new ones here instead of deploying.
            </p>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={handleAdd} className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
          <div className="text-sm font-medium text-gray-700 mb-3">Add city</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="City name (e.g. oakland)"
              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <input
              type="text"
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="Aliases, comma-separated (e.g. oak, east bay)"
              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={adding || !name.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md transition-colors"
            >
              {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Add
            </button>
          </div>
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </form>

        {/* Filter */}
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter cities..."
          className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />

        {/* City list */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">No cities found.</div>
          ) : (
            filtered.map((city) => (
              <div
                key={city.id}
                className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${city.active ? "bg-green-500" : "bg-gray-300"}`} />
                  <span className="text-sm text-gray-900 font-medium">{city.name}</span>
                  {city.aliases.length > 0 && (
                    <span className="text-xs text-gray-400 truncate">
                      {city.aliases.join(", ")}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(city)}
                  className="text-gray-300 hover:text-red-500 transition-colors ml-3 shrink-0"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))
          )}
        </div>

        <p className="text-xs text-gray-400 mt-3 text-right">{cities.length} cities</p>
      </div>
    </AppLayout>
  );
}
